import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUpIcon, HomeIcon, LandPlotIcon, BuildingIcon, HammerIcon } from 'lucide-react';
type DealCategory = 'all' | 'cash-flow' | 'equity' | 'land' | 'commercial' | 'pre-construction';
interface Deal {
  id: number;
  category: DealCategory;
  type: string;
  location: string;
  price: string;
  annualRevenue?: string;
  cashFlow?: string;
  equityUpside?: string;
  details: string;
  badge: string;
}
export function CurrentDeals() {
  const [activeCategory, setActiveCategory] = useState<DealCategory>('all');
  const deals: Deal[] = [{
    id: 1,
    category: 'cash-flow',
    type: '3BR/3.5BA Condo',
    location: 'Marina Golden Zone',
    price: '$315,000',
    annualRevenue: '$62,000',
    cashFlow: '11.2%',
    details: 'Fully furnished, ocean view, 320 sqm, proven rental history',
    badge: 'High Yield'
  }, {
    id: 2,
    category: 'equity',
    type: '4BR Villa',
    location: 'Pedregal',
    price: '$1,950,000',
    equityUpside: '$1.2M',
    details: 'Buy $1.95M, invest $450K renovation, ARV $3.6M',
    badge: 'Equity Play'
  }, {
    id: 3,
    category: 'cash-flow',
    type: '2BR/2BA Condo',
    location: 'Medano Beach',
    price: '$285,000',
    annualRevenue: '$54,000',
    cashFlow: '10.8%',
    details: 'Beachfront, turnkey, managed by top rental company',
    badge: 'Turnkey'
  }, {
    id: 4,
    category: 'land',
    type: 'Ocean View Lot',
    location: 'East Cape',
    price: '$425,000',
    equityUpside: '$600K',
    details: '2,400 sqm, utilities ready, 5-year appreciation avg: 18%/yr',
    badge: 'Land Bank'
  }, {
    id: 5,
    category: 'commercial',
    type: 'Retail Space',
    location: 'Marina Plaza',
    price: '$890,000',
    annualRevenue: '$98,000',
    cashFlow: '7.4%',
    details: '180 sqm, triple net lease, established tenant, 8-year term',
    badge: 'NNN Lease'
  }, {
    id: 6,
    category: 'pre-construction',
    type: '3BR Penthouse',
    location: 'Corridor',
    price: '$625,000',
    equityUpside: '$280K',
    details: 'Pre-construction pricing, delivery Q2 2027, est. market value $905K',
    badge: 'Pre-Sale'
  }, {
    id: 7,
    category: 'equity',
    type: '5BR Estate',
    location: 'Pacific Side',
    price: '$2,400,000',
    equityUpside: '$1.8M',
    details: 'Distressed sale, needs $600K work, comparable sales $4.8M',
    badge: 'Fixer-Upper'
  }, {
    id: 8,
    category: 'cash-flow',
    type: '1BR/1BA Condo',
    location: 'Marina',
    price: '$195,000',
    annualRevenue: '$38,000',
    cashFlow: '12.1%',
    details: 'Studio conversion, high occupancy area, managed',
    badge: 'Best ROI'
  }, {
    id: 9,
    category: 'land',
    type: 'Development Parcel',
    location: 'Corridor',
    price: '$1,650,000',
    equityUpside: '$2.1M',
    details: '8,500 sqm, zoned for 12 units, infrastructure in place',
    badge: 'Developer'
  }, {
    id: 10,
    category: 'commercial',
    type: 'Restaurant Space',
    location: 'Downtown',
    price: '$1,200,000',
    annualRevenue: '$156,000',
    cashFlow: '8.9%',
    details: 'Established location, existing tenant, percentage lease',
    badge: 'Food & Bev'
  }];
  const categories = [{
    id: 'all' as DealCategory,
    label: 'All Deals',
    icon: TrendingUpIcon
  }, {
    id: 'cash-flow' as DealCategory,
    label: 'Best Cash-Flowing',
    icon: HomeIcon
  }, {
    id: 'equity' as DealCategory,
    label: 'Equity Plays',
    icon: HammerIcon
  }, {
    id: 'land' as DealCategory,
    label: 'Vacant Land',
    icon: LandPlotIcon
  }, {
    id: 'commercial' as DealCategory,
    label: 'Commercial',
    icon: BuildingIcon
  }, {
    id: 'pre-construction' as DealCategory,
    label: 'Pre-Construction',
    icon: TrendingUpIcon
  }];
  const filteredDeals = activeCategory === 'all' ? deals : deals.filter(deal => deal.category === activeCategory);
  const scrollToReport = () => {
    const element = document.getElementById('deal-report');
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  return <section id="deals" className="py-24 bg-charcoal" data-id="element-71">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-72">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-73">
          <h2 className="text-4xl sm:text-5xl font-serif font-bold text-white mb-6" data-id="element-74">
            Current Deal Flow
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto" data-id="element-75">
            Curated opportunities with verified data. Full analysis available in
            the Deal Report.
          </p>
        </motion.div>

        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-3 mb-12" data-id="element-76">
          {categories.map(category => <button key={category.id} onClick={() => setActiveCategory(category.id)} className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${activeCategory === category.id ? 'bg-gold text-navy' : 'bg-navy/50 text-white hover:bg-navy/70'}`} data-id="element-77">
              <category.icon size={18} data-id="element-78" />
              <span className="hidden sm:inline" data-id="element-79">{category.label}</span>
              <span className="sm:hidden" data-id="element-80">{category.label.split(' ')[0]}</span>
            </button>)}
        </div>

        {/* Deal Cards */}
        <AnimatePresence mode="wait" data-id="element-81">
          <motion.div key={activeCategory} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-82">
            {filteredDeals.map((deal, index) => <motion.div key={deal.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="bg-navy rounded-lg overflow-hidden border border-white/10 hover:border-gold/50 transition-all" data-id="element-83">
                <div className="p-6" data-id="element-84">
                  <div className="flex justify-between items-start mb-4" data-id="element-85">
                    <div data-id="element-86">
                      <h3 className="text-xl font-serif font-semibold text-white mb-1" data-id="element-87">
                        {deal.type}
                      </h3>
                      <p className="text-white/60 text-sm" data-id="element-88">{deal.location}</p>
                    </div>
                    <span className="bg-gold/20 text-gold px-3 py-1 rounded text-xs font-semibold" data-id="element-89">
                      {deal.badge}
                    </span>
                  </div>

                  <div className="space-y-3 mb-6" data-id="element-90">
                    <div className="flex justify-between items-center py-2 border-b border-white/10" data-id="element-91">
                      <span className="text-white/60 text-sm" data-id="element-92">Price</span>
                      <span className="text-white font-bold font-mono" data-id="element-93">
                        {deal.price}
                      </span>
                    </div>
                    {deal.annualRevenue && <div className="flex justify-between items-center py-2 border-b border-white/10" data-id="element-94">
                        <span className="text-white/60 text-sm" data-id="element-95">
                          Est. Annual Revenue
                        </span>
                        <span className="text-gold font-bold font-mono" data-id="element-96">
                          {deal.annualRevenue}
                        </span>
                      </div>}
                    {deal.cashFlow && <div className="flex justify-between items-center py-2 border-b border-white/10" data-id="element-97">
                        <span className="text-white/60 text-sm" data-id="element-98">
                          Cash-on-Cash Return
                        </span>
                        <span className="text-gold font-bold font-mono" data-id="element-99">
                          {deal.cashFlow}
                        </span>
                      </div>}
                    {deal.equityUpside && <div className="flex justify-between items-center py-2 border-b border-white/10" data-id="element-100">
                        <span className="text-white/60 text-sm" data-id="element-101">
                          Equity Upside
                        </span>
                        <span className="text-gold font-bold font-mono" data-id="element-102">
                          {deal.equityUpside}
                        </span>
                      </div>}
                  </div>

                  <p className="text-white/70 text-sm mb-6 leading-relaxed" data-id="element-103">
                    {deal.details}
                  </p>

                  <button onClick={scrollToReport} className="w-full bg-gold/10 hover:bg-gold text-gold hover:text-navy border border-gold px-4 py-3 rounded font-semibold transition-all text-sm" data-id="element-104">
                    Full Analysis in Deal Report
                  </button>
                </div>
              </motion.div>)}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}